import React, { useState, useEffect } from 'react';
import { BudgetInput } from '../../../components/shared/BudgetInput';
import BudgetStepWrapper from './BudgetStepWrapper';
import { AlertTriangle, Lock } from 'lucide-react';
import { useAppContext } from '../../../core/App';

// --- Budget Step 2: Needs ---
const BudgetStep2 = ({ needs, setNeeds, handleChange, onNext, onPrev, isSurvivalZone, location, avgRent, takeHome }) => {
  const [clickCount, setClickCount] = useState(0);
  const { setToastMessage, logResilienceFailure } = useAppContext();

  const handleFixedFieldClick = () => {
    if (!isSurvivalZone) return;

    setClickCount(prev => prev + 1);
    
    // Trigger Frustration Spasm if user clicks a read-only field 3x in rapid succession
    if (clickCount >= 2) {
      setToastMessage("ADMIN ALERT: Fixed cost detected. Rent in this market is non-negotiable for this simulation.");
      logResilienceFailure({ frustrationDelta: 0.15, jiggleMetric: true });
      setClickCount(0);
    }
  };

  // Reset click count after 2 seconds of inactivity
  useEffect(() => {
    if (clickCount > 0) {
      const timer = setTimeout(() => setClickCount(0), 2000);
      return () => clearTimeout(timer);
    }
  }, [clickCount]);

  // Ensure needs.rent is forced to avgRent if in Survival Zone
  useEffect(() => {
    if (isSurvivalZone && needs.rent !== avgRent) {
      setNeeds(prev => ({ ...prev, rent: avgRent }));
    }
  }, [isSurvivalZone, avgRent, needs.rent, setNeeds]);

  return (
    <BudgetStepWrapper title="Establish Fixed Logistical Baselines" description="Input non-negotiable regional baseline costs." onNext={onNext} onPrev={onPrev}>
      
      {/* THE REDLINE INTERVENTION & LOCKED REALITY */}
      <div className={`transition-all duration-300 relative ${isSurvivalZone ? "p-5 bg-red-50 border-2 border-red-200 rounded-xl shadow-inner mt-2 col-span-full sm:col-span-2" : ""}`}>
          {isSurvivalZone && (
              <div className="absolute -top-3 left-4 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1 shadow-md z-10">
                  <AlertTriangle size={12} /> Market Standard Redline
              </div>
          )}
          
          <div className="relative group">
            <BudgetInput 
              label="Rent/Mortgage" 
              id="rent" 
              value={isSurvivalZone ? avgRent : needs.rent} 
              onChange={v => handleChange(setNeeds, 'rent', v)}
              readOnly={isSurvivalZone}
              onClick={handleFixedFieldClick}
              className={isSurvivalZone ? "bg-slate-100 border-red-200 text-slate-500 cursor-not-allowed select-none" : ""}
            />
            {isSurvivalZone && (
              <div className="mt-2 flex items-center gap-1.5 text-[10px] font-black text-red-600 uppercase tracking-widest animate-pulse">
                <Lock size={10} /> Market Lock: No cheaper options detected in {location === 'default' || location === 'Other' ? 'your area' : location}
              </div>
            )}
          </div>
          
          {isSurvivalZone && takeHome > 0 && (
              <p className="text-xs text-red-700 mt-4 font-medium bg-red-100/50 p-2 rounded-lg border border-red-100">
                  Average rent in {location === 'default' || location === 'Other' ? 'your area' : location} will consume <span className="font-black text-red-900">{(avgRent/takeHome * 100).toFixed(0)}%</span> of your income.
              </p>
          )}
      </div>

      <BudgetInput label="Utilities (Electric, Water, Gas, Internet)" id="utilities" value={needs.utilities} onChange={v => handleChange(setNeeds, 'utilities', v)} />
      <BudgetInput label="Minimum Student Loan Payments" id="studentLoan" value={needs.studentLoan} onChange={v => handleChange(setNeeds, 'studentLoan', v)} />
      <BudgetInput label="Car Payment" id="carPayment" value={needs.carPayment} onChange={v => handleChange(setNeeds, 'carPayment', v)} />
      <BudgetInput label="Car & Renter's Insurance" id="insurance" value={needs.insurance} onChange={v => handleChange(setNeeds, 'insurance', v)} />
      <BudgetInput label="Subscriptions (Netflix, Spotify, etc.)" id="subscriptions" value={needs.subscriptions} onChange={v => handleChange(setNeeds, 'subscriptions', v)} />
    </BudgetStepWrapper>
  );
};

export default BudgetStep2;